import type { LeadRow } from "@gioia365/contracts";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { LoadedPage } from "../lead-list-hooks";
import { bulkLeadName } from "./bulk-text";

// Le caselle delle «Azioni multiple» (piano M3 Task 14, D10; mock-up `initLeadPage`): i Lead spuntati uno per uno, oppure
// «Seleziona tutti i N risultati», cioè la vista di adesso (ricerca, «Di chi» e filtri, decisione P1), che il server
// risolve al momento della conferma. Le righe arrivano a pagine e soltanto quelle nella finestra restano lette: la
// scelta non dipende da ciò che si vede, così scorrere non la cambia. Un Lead spuntato resta finché l'elenco lo mostra:
// - se ricerca, «Di chi» o filtri cambiano (un altro elenco), restano spuntati soltanto i Lead che le pagine arrivate
//   dell'elenco nuovo hanno (gli altri non si vedono più, e un'azione non tocca un Lead che non si vede spuntato);
// - se la pagina dove lo si era visto si rilegge senza di lui (un collega l'ha cambiato o archiviato), esce dalla scelta;
// - l'ordinamento e le colonne non cambiano i Lead dell'elenco: la scelta resta.
// «Tutti i risultati» vale per la vista in cui lo si è scelto: con un'altra vista la scelta torna vuota.

/** Dove un Lead spuntato si è visto l'ultima volta: l'elenco, la lettura (con l'ordinamento), la pagina e quando. */
type Seen = { list: string; read: string; page: number; at: number };

/** Un Lead spuntato, con ciò che la finestra di conferma ne dice: il nome e i tag («Togli tag» li conta). */
export type PickedLead = { id: string; name: string; tags: LeadRow["tags"]; seen: Seen };

export type BulkSelection =
  | { mode: "ids"; list: string; picked: ReadonlyMap<string, PickedLead> }
  /** «Seleziona tutti i N risultati»: la vista `list`. */
  | { mode: "all"; list: string };

/** La vista di adesso: l'elenco (ricerca, «Di chi», filtri), la lettura (con ordinamento e colonne) e le sue pagine arrivate. */
export type SelectionView = { list: string; read: string; pages: readonly LoadedPage[] };

/** La casella «Seleziona tutti i Lead mostrati»: le righe arrivate tutte spuntate, alcune, nessuna. */
export type PageState = "none" | "some" | "all";

function emptySelection(list: string): BulkSelection {
  return { mode: "ids", list, picked: new Map() };
}

function sameTags(a: LeadRow["tags"], b: LeadRow["tags"]): boolean {
  return a.length === b.length && a.every((tag, index) => tag.id === b[index]?.id && tag.name === b[index]?.name);
}

/** Dove sta ogni Lead nelle pagine arrivate. */
function locate(pages: readonly LoadedPage[]): Map<string, { row: LeadRow; page: LoadedPage }> {
  const where = new Map<string, { row: LeadRow; page: LoadedPage }>();
  for (const page of pages) for (const row of page.rows) if (!where.has(row.id)) where.set(row.id, { row, page });
  return where;
}

/**
 * Un Lead appena spuntato. Senza la sua pagina (spuntato fra le righe della vista di prima, mostrate mentre arriva la
 * nuova) non si sa in quale elenco stia: resta spuntato soltanto se l'elenco nuovo lo mostra.
 */
function pickedOf(row: LeadRow, page: LoadedPage | undefined, view: SelectionView): PickedLead {
  const seen: Seen = page ? { list: view.list, read: view.read, page: page.page, at: page.updatedAt } : { list: "", read: "", page: -1, at: 0 };
  return { id: row.id, name: bulkLeadName(row), tags: row.tags, seen };
}

/**
 * La scelta confrontata con la vista di adesso (vedi sopra). Restituisce lo stesso oggetto quando non cambia nulla:
 * chi la usa la confronta per decidere se salvarla.
 */
export function reconcileSelection(selection: BulkSelection, view: SelectionView): BulkSelection {
  if (selection.mode === "all") return selection.list === view.list ? selection : emptySelection(view.list);
  if (selection.picked.size === 0) return selection.list === view.list ? selection : emptySelection(view.list);
  // Le pagine della vista nuova non sono ancora arrivate: non si sa ancora chi c'è.
  if (view.pages.length === 0) return selection;
  const where = locate(view.pages);
  let changed = selection.list !== view.list;
  const next = new Map<string, PickedLead>();
  for (const picked of selection.picked.values()) {
    const found = where.get(picked.id);
    if (found) {
      const { row, page } = found;
      const seen = picked.seen;
      const name = bulkLeadName(row);
      const same = seen.list === view.list && seen.read === view.read && seen.page === page.page && seen.at === page.updatedAt && picked.name === name && sameTags(picked.tags, row.tags);
      const kept = same ? picked : pickedOf(row, page, view);
      if (!same) changed = true;
      next.set(picked.id, kept);
      continue;
    }
    const otherList = picked.seen.list !== view.list;
    const rereadWithout = picked.seen.read === view.read && view.pages.some((page) => page.page === picked.seen.page && page.updatedAt > picked.seen.at);
    if (otherList || rereadWithout) {
      changed = true;
      continue;
    }
    next.set(picked.id, picked);
  }
  return changed ? { mode: "ids", list: view.list, picked: next } : selection;
}

/**
 * La scelta delle azioni multiple per la vista di adesso: chi è spuntato, le caselle di riga e di pagina, «Seleziona
 * tutti i N risultati» e «Deseleziona». Le funzioni restano le stesse fra un disegno e l'altro (le righe della tabella
 * non si ridisegnano per questo).
 */
export function useBulkSelection(view: SelectionView) {
  const [state, setState] = useState<BulkSelection>(() => emptySelection(view.list));
  // La scelta segue la vista mentre si disegna (come `useResultSearch`): nessun disegno con Lead che non ci sono più.
  const selection = reconcileSelection(state, view);
  if (selection !== state) setState(selection);

  // L'ultima vista disegnata, per le funzioni che restano le stesse.
  const viewRef = useRef(view);
  useLayoutEffect(() => {
    viewRef.current = view;
  });

  const pick = useCallback((row: LeadRow, checked: boolean) => {
    setState((current) => {
      const now = viewRef.current;
      const base = reconcileSelection(current, now);
      if (base.mode === "all") return base;
      const next = new Map(base.picked);
      if (checked) next.set(row.id, pickedOf(row, now.pages.find((page) => page.rows.some((candidate) => candidate.id === row.id)), now));
      else next.delete(row.id);
      return { mode: "ids", list: now.list, picked: next };
    });
  }, []);

  /** «Seleziona tutti i Lead mostrati»: spunta (o toglie) tutte le righe arrivate. */
  const pickPage = useCallback((checked: boolean) => {
    setState((current) => {
      const now = viewRef.current;
      const base = reconcileSelection(current, now);
      if (base.mode === "all") return base;
      const next = new Map(base.picked);
      for (const page of now.pages) {
        for (const row of page.rows) {
          if (checked) next.set(row.id, pickedOf(row, page, now));
          else next.delete(row.id);
        }
      }
      return { mode: "ids", list: now.list, picked: next };
    });
  }, []);

  const selectAll = useCallback(() => setState({ mode: "all", list: viewRef.current.list }), []);
  const clear = useCallback(() => setState(emptySelection(viewRef.current.list)), []);

  const pickedIds = useMemo<ReadonlySet<string> | "all">(() => (selection.mode === "all" ? "all" : new Set(selection.picked.keys())), [selection]);

  let page: PageState = "none";
  if (selection.mode === "all") page = "all";
  else {
    const rows = view.pages.flatMap((loaded) => loaded.rows);
    const ticked = rows.filter((row) => selection.picked.has(row.id)).length;
    if (ticked > 0) page = ticked === rows.length ? "all" : "some";
  }

  // La vista è cambiata e le sue pagine non sono ancora arrivate (o non arrivano): la scelta tiene ancora i Lead dell'elenco di
  // prima, e le azioni aspettano (rilievo di Codex sulla #64, turno di notte del 10 ottobre 2026).
  const pending = selection.mode === "ids" && selection.picked.size > 0 && selection.list !== view.list;

  return { selection, pickedIds, page, pending, pick, pickPage, selectAll, clear };
}

export type BulkSelectionState = ReturnType<typeof useBulkSelection>;
